// ============================================================================
// Facility Health Widget — donut chart of facilities by RAG status, with
// counts list to the right. RAG colours pulled from design-tokens so the
// dashboard stays in lockstep with Account Management.
//
// Why hand-rolled SVG instead of a chart library: the project doesn't have
// recharts/d3 installed (verified May 2026), and a donut at this scale is
// ~30 lines of arc math. Adds zero bundle weight.
// ============================================================================

import { RAG_COLORS } from '../../shared/design-tokens';
import type { FacilityHealthBuckets } from './use-dashboard-data';
import { WidgetCard, WidgetSkeleton } from './widget-card';

interface Props {
  data: FacilityHealthBuckets | null;
  loading: boolean;
}

// Slice colours mirror the RAG palette plus a neutral grey for unset RAG.
const SLICE_COLORS: Record<keyof Omit<FacilityHealthBuckets, 'total'>, string> =
  {
    green: RAG_COLORS.Green,
    amber: RAG_COLORS.Amber,
    red: RAG_COLORS.Red,
    unset: '#CBD5E1', // slate-300 — visibly distinct from the RAG triad
  };

const SLICE_LABELS: Record<keyof Omit<FacilityHealthBuckets, 'total'>, string> =
  {
    green: 'Green',
    amber: 'Amber',
    red: 'Red',
    unset: 'Unset',
  };

export function FacilityHealthWidget({ data, loading }: Props) {
  if (loading && !data) {
    return (
      <WidgetCard title="Facility health" minHeight={240}>
        <WidgetSkeleton lines={4} />
      </WidgetCard>
    );
  }

  if (!data) {
    return (
      <WidgetCard title="Facility health" minHeight={240}>
        <p className="text-sm text-tcs-text-muted">No facility data available.</p>
      </WidgetCard>
    );
  }

  const slices: Array<{
    key: keyof Omit<FacilityHealthBuckets, 'total'>;
    value: number;
  }> = [
    { key: 'green', value: data.green },
    { key: 'amber', value: data.amber },
    { key: 'red', value: data.red },
    { key: 'unset', value: data.unset },
  ];
  const visibleSlices = slices.filter((s) => s.value > 0);

  return (
    <WidgetCard
      title="Facility health"
      subtitle={`${data.total} facilities — RAG distribution`}
      minHeight={240}
    >
      <div className="flex items-center gap-6">
        <Donut slices={visibleSlices} total={data.total} />
        <ul className="flex-1 space-y-1.5 text-sm">
          {slices.map((s) => {
            const pct =
              data.total > 0 ? Math.round((s.value / data.total) * 100) : 0;
            return (
              <li
                key={s.key}
                className="flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: SLICE_COLORS[s.key] }}
                    aria-hidden
                  />
                  <span className="text-tcs-text-secondary">{SLICE_LABELS[s.key]}</span>
                </div>
                <div className="text-right tabular-nums">
                  <span className="font-medium text-tcs-text">{s.value}</span>
                  <span className="ml-1 text-xs text-tcs-text-muted">{pct}%</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      {data.red > 0 && (
        <p className="mt-3 text-xs text-rag-red">
          {data.red} red {data.red === 1 ? 'facility needs' : 'facilities need'}{' '}
          attention.
        </p>
      )}
    </WidgetCard>
  );
}

// ----- Donut SVG ------------------------------------------------------------
// Renders a donut using one <path> per slice. Each slice is an annular sector:
// outer arc minus inner arc, sweep flag chosen so big slices > 180° render
// correctly. Total label sits centred in the hole.

interface DonutProps {
  slices: Array<{
    key: keyof Omit<FacilityHealthBuckets, 'total'>;
    value: number;
  }>;
  total: number;
}

function Donut({ slices, total }: DonutProps) {
  const SIZE = 140;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const OUTER = 60;
  const INNER = 38;
  const sum = slices.reduce((acc, s) => acc + s.value, 0);

  // Single-slice case (everything in one bucket): SVG arcs from 0 to 2π are
  // ambiguous (start == end), so render a full ring instead of a path.
  if (slices.length === 1 && sum > 0) {
    return (
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`Donut chart: ${total} facilities`}
      >
        <circle
          cx={CX}
          cy={CY}
          r={(OUTER + INNER) / 2}
          fill="none"
          strokeWidth={OUTER - INNER}
          stroke={SLICE_COLORS[slices[0].key]}
        />
        <CenterLabel total={total} />
      </svg>
    );
  }

  let cursor = -Math.PI / 2; // start at 12 o'clock for an even visual baseline
  const paths = slices.map((s) => {
    const angle = sum > 0 ? (s.value / sum) * Math.PI * 2 : 0;
    const start = cursor;
    const end = cursor + angle;
    cursor = end;
    return {
      key: s.key,
      d: arcPath(CX, CY, OUTER, INNER, start, end),
      color: SLICE_COLORS[s.key],
    };
  });

  return (
    <svg
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label={`Donut chart: ${total} facilities`}
    >
      {paths.map((p) => (
        <path key={p.key} d={p.d} fill={p.color} />
      ))}
      <CenterLabel total={total} />
    </svg>
  );
}

function CenterLabel({ total }: { total: number }) {
  return (
    <>
      <text
        x="50%"
        y="46%"
        textAnchor="middle"
        className="fill-tcs-text"
        style={{ fontSize: 22, fontWeight: 600 }}
      >
        {total}
      </text>
      <text
        x="50%"
        y="62%"
        textAnchor="middle"
        className="fill-tcs-text-muted"
        style={{ fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}
      >
        Facilities
      </text>
    </>
  );
}

// Build an annular sector path from `outer` arc and `inner` arc (reversed) +
// two radial connectors. `sweep > π` triggers the large-arc flag so SVG draws
// the longer side of the arc.
function arcPath(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  start: number,
  end: number,
): string {
  const sweep = end - start;
  const large = sweep > Math.PI ? 1 : 0;
  const x0 = cx + outer * Math.cos(start);
  const y0 = cy + outer * Math.sin(start);
  const x1 = cx + outer * Math.cos(end);
  const y1 = cy + outer * Math.sin(end);
  const x2 = cx + inner * Math.cos(end);
  const y2 = cy + inner * Math.sin(end);
  const x3 = cx + inner * Math.cos(start);
  const y3 = cy + inner * Math.sin(start);
  return [
    `M ${x0} ${y0}`,
    `A ${outer} ${outer} 0 ${large} 1 ${x1} ${y1}`,
    `L ${x2} ${y2}`,
    `A ${inner} ${inner} 0 ${large} 0 ${x3} ${y3}`,
    'Z',
  ].join(' ');
}
